/** Feature-intro registry. Each entry describes a single feature
 *  surfaced to RETURNING users (anyone with `users.onboarded_at`
 *  set) the first time after the feature ships. Distinct from the
 *  first-run onboarding wizard at /onboarding, which only fires for
 *  brand-new accounts.
 *
 *  Adding a new feature intro:
 *    1. Append a FeatureIntro to FEATURE_INTROS below with a fresh
 *       feature_key (snake_case). The key is the dismissal-tracking
 *       primary key in user_feature_intros and must never be reused
 *       across features.
 *    2. introducedAt: ISO date the feature shipped. The overlay
 *       skips intros older than the user's onboarded_at minus a
 *       small grace window — a user who onboarded yesterday should
 *       not get peppered with intros for features that shipped two
 *       months ago and they implicitly already use.
 *    3. cta + ctaRoute drive the "Set it up" button. Omit to render
 *       an info-only banner with a "Got it" button.
 *
 *  Removing an intro: leave the entry in place for one full release
 *  after every active user has dismissed it; only then can the key
 *  be retired. Out-of-registry feature_keys in user_feature_intros
 *  are harmless — they sit dormant. */

export interface FeatureIntro {
  /** Stable snake_case key. Primary key in user_feature_intros. */
  key: string;
  /** Shipping date. Users onboarded before this date get the intro;
   *  users onboarded after silently skip it. ISO date string. */
  introducedAt: string;
  /** Short title for the overlay. Memso brand voice: no em dashes,
   *  no "brain" terminology. */
  title: string;
  /** One- to three-sentence summary of what the feature does. */
  summary: string;
  /** Optional call-to-action label. If unset, the overlay renders
   *  only the "Got it" dismiss button. */
  cta?: string;
  /** Optional in-app route the CTA navigates to. Required if `cta`
   *  is set. */
  ctaRoute?: string;
}

/** Canonical catalogue. Order matters: when multiple intros are
 *  pending, the overlay shows them oldest-first so the user gets
 *  features in the order they shipped. */
export const FEATURE_INTROS: FeatureIntro[] = [
  // Future entries land here. Example shape (commented to avoid
  // surfacing a placeholder card on the first deploy):
  //
  // {
  //   key: "weave_synthesis_v2_3",
  //   introducedAt: "2026-05-10",
  //   title: "New: Memso weaves your meetings into one view",
  //   summary:
  //     "Memso now synthesizes follow-ups across meetings and email into a single living brief per engagement. Open any person card to see it.",
  //   cta: "Show me",
  //   ctaRoute: "/u/wwahmed",
  // },
];

/** Filter the registry down to intros that should be shown to a
 *  specific user, given their dismissed-set and onboarded_at. Pure
 *  function so it's trivial to unit-test. */
export function pendingFeatureIntros(input: {
  dismissedKeys: Set<string>;
  onboardedAt: string | null;
}): FeatureIntro[] {
  const onboarded = input.onboardedAt ? new Date(input.onboardedAt).getTime() : null;
  return FEATURE_INTROS.filter((f) => {
    if (input.dismissedKeys.has(f.key)) return false;
    if (onboarded == null) return false; // first-run users get the wizard, not feature intros
    const shipped = new Date(f.introducedAt).getTime();
    return shipped >= onboarded;
  });
}
